import type { ReactElement, ReactNode } from "react";

import { Badge } from "./Badge.js";

/**
 * TuileKpi — tuile de statistique cliquable : (icône) + titre + chiffre + détail
 * (+ badge d'alerte). Brique partagée du dashboard et des hubs (Ventes…).
 *
 * PRÉSENTATION PURE + agnostique du routeur : `@champ-erp/ui` ne dépend pas de
 * react-router. L'interactivité (navigation SPA `<Link>`, bouton-filtre, ou tuile
 * statique) est déléguée au caller via `renderContainer`, qui reçoit la className
 * de shell (bordure/hover/état actif) + le contenu et retourne l'élément wrapper :
 *   - Navigation : `({ className, children }) => <Link to={to} className={className}>{children}</Link>`
 *   - Filtre     : `({ className, children }) => <button type="button" onClick={…} aria-pressed={actif} className={className}>{children}</button>`
 *   - Statique   : (aucun `renderContainer`) → rendu dans un simple `<div>`.
 */
export interface TuileKpiProps {
  /** Icône optionnelle affichée avant le titre. */
  icon?: ReactNode;
  /** Intitulé court en capitales (ex. « CA du mois »). */
  titre: string;
  /** Valeur mise en avant (déjà formatée : « 4 820 € », « 12 »…). */
  valeur: string;
  /** Précision sous la valeur (ex. « HT · factures émises »). */
  detail?: string;
  /** Libellé d'alerte (badge rust, ex. « 2 en retard »). */
  alerte?: string;
  /** Surbrillance « sélectionnée » (tuile-filtre active). */
  actif?: boolean;
  /**
   * Conteneur cliquable optionnel. Absent → `<div>` non interactif. Voir la
   * doc du composant pour les patterns Link/bouton.
   */
  renderContainer?: (args: { className: string; children: ReactNode }) => ReactElement;
  className?: string;
}

export function TuileKpi({
  icon,
  titre,
  valeur,
  detail,
  alerte,
  actif = false,
  renderContainer,
  className = "",
}: TuileKpiProps) {
  // Une tuile n'affiche des affordances de survol que si elle est interactive
  // (présence d'un conteneur cliquable) — sinon le hover serait trompeur.
  const interactive = renderContainer !== undefined;

  const shellClass = [
    "flex min-h-[104px] flex-col rounded-xl border p-4 text-left shadow-sm",
    interactive ? "transition motion-reduce:transition-none" : "",
    actif
      ? "border-champagne-400 bg-champagne-50/70 ring-1 ring-champagne-300"
      : interactive
        ? "border-champagne-200 bg-white hover:border-champagne-300 hover:bg-champagne-50/50"
        : "border-champagne-200 bg-white",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const contenu = (
    <>
      <div className="flex items-center gap-1.5 text-champagne-600">
        {icon && <span className="text-champagne-500">{icon}</span>}
        <span className="text-[12px] font-medium uppercase tracking-wide text-champagne-700">
          {titre}
        </span>
      </div>
      {/* 16 px sous `sm` : ces tuiles s'affichent sur 2 colonnes en mobile, soit
          ~92 px utiles sur un petit téléphone (360 px) — un montant comme
          « 3 251,04 € » demandait 121 px en 22 px et se retrouvait rogné. Un
          chiffre d'argent doit se lire en entier : on réduit la taille plutôt
          que de couper, et on retrouve 22 px dès que la place existe. */}
      <div className="mono tnum mt-2 text-[16px] font-semibold text-aubergine-900 sm:text-[22px]">
        {valeur}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {detail && <span className="text-[12px] text-champagne-700">{detail}</span>}
        {alerte && (
          <Badge variant="danger" size="sm" dot>
            {alerte}
          </Badge>
        )}
      </div>
    </>
  );

  if (renderContainer) {
    return renderContainer({ className: shellClass, children: contenu });
  }
  return <div className={shellClass}>{contenu}</div>;
}
